import React, { useEffect, useState, useMemo } from 'react';

interface LedgerPanelProps {
    kamokuId: number;
    kamokuName: string;
    hojoId?: number | null;
    hojoName?: string | null;
    fiscalYear: number;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    onClose: () => void;
    style?: React.CSSProperties;
}

interface LedgerTransaction {
    id: number;
    date: string;
    description: string;
    contra_kamoku_name: string;
    deposit_amount: number;
    withdrawal_amount: number;
    balance: number;
}

interface LedgerResponse {
    kamoku_id: number;
    date_from: string;
    date_to: string;
    previous_balance: number;
    transactions: LedgerTransaction[];
    total_deposit: number;
    total_withdrawal: number;
    next_balance: number;
}

const LedgerPanel: React.FC<LedgerPanelProps> = ({
    kamokuId,
    kamokuName,
    hojoId,
    hojoName,
    fiscalYear,
    apiFetch,
    onClose,
    style
}) => {
    const [ledger, setLedger] = useState<LedgerResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Calculate dates based on fiscal year (April 1st to March 31st next year)
    const { startDate, endDate } = useMemo(() => {
        return {
            startDate: `${fiscalYear}-04-01`,
            endDate: `${fiscalYear + 1}-03-31`
        };
    }, [fiscalYear]);

    useEffect(() => {
        const fetchLedger = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({
                    kamoku_id: kamokuId.toString(),
                    date_from: startDate,
                    date_to: endDate
                });
                if (hojoId) {
                    params.append('hojo_id', hojoId.toString());
                }

                const response = await apiFetch(`/api/ledgers?${params.toString()}`);
                if (!response.ok) {
                    throw new Error('元帳データの取得に失敗しました');
                }
                const data = await response.json();
                setLedger(data);
            } catch (err: any) {
                console.error(err);
                setError(err.message || 'データ取得エラー');
            } finally {
                setLoading(false);
            }
        };

        if (kamokuId) {
            fetchLedger();
        }
    }, [kamokuId, hojoId, startDate, endDate, apiFetch]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ja-JP').format(amount);
    };

    // Default fixed styles
    const defaultStyles: React.CSSProperties = {
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: '600px',
        height: 'calc(100vh - 60px)',
        backgroundColor: '#fff',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.2)',
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #ccc'
    };

    return (
        <div style={{ ...defaultStyles, ...style }}>
            {/* Header */}
            <div style={{
                padding: '10px 15px',
                backgroundColor: '#f0f0f0',
                borderBottom: '1px solid #ccc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '14px' }}>
                        {kamokuName} {hojoName ? `(${hojoName})` : ''} 元帳
                    </h3>
                    <div style={{ fontSize: '11px', color: '#666' }}>
                        {fiscalYear}年度 ({startDate} 〜 {endDate})
                    </div>
                </div>
                <button onClick={onClose} style={{ cursor: 'pointer', border: 'none', background: 'transparent', fontSize: '16px' }}>
                    ✕
                </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                {loading && <div style={{ textAlign: 'center', padding: '20px' }}>読み込み中...</div>}
                {error && <div style={{ color: 'red', padding: '10px' }}>{error}</div>}

                {ledger && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff' }}>
                            <tr style={{ backgroundColor: '#eef' }}>
                                <th style={{ border: '1px solid #ccc', padding: '4px' }}>日付</th>
                                <th style={{ border: '1px solid #ccc', padding: '4px' }}>摘要</th>
                                <th style={{ border: '1px solid #ccc', padding: '4px' }}>借方</th>
                                <th style={{ border: '1px solid #ccc', padding: '4px' }}>貸方</th>
                                <th style={{ border: '1px solid #ccc', padding: '4px' }}>残高</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colSpan={4} style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>前期繰越</td>
                                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                    {formatCurrency(ledger.previous_balance)}
                                </td>
                            </tr>
                            {ledger.transactions.map((tx) => (
                                <tr key={tx.id}>
                                    <td style={{ border: '1px solid #ccc', padding: '4px' }}>{tx.date}</td>
                                    <td style={{ border: '1px solid #ccc', padding: '4px' }}>{tx.description}</td>
                                    <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                        {tx.deposit_amount !== 0 ? formatCurrency(tx.deposit_amount) : ''}
                                    </td>
                                    <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                        {tx.withdrawal_amount !== 0 ? formatCurrency(tx.withdrawal_amount) : ''}
                                    </td>
                                    <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                        {formatCurrency(tx.balance)}
                                    </td>
                                </tr>
                            ))}
                            {ledger.transactions.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', padding: '10px', color: '#999' }}>
                                        取引なし
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot style={{ position: 'sticky', bottom: 0, backgroundColor: '#fff' }}>
                            <tr style={{ backgroundColor: '#eef', fontWeight: 'bold' }}>
                                <td colSpan={2} style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>合計</td>
                                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                    {formatCurrency(ledger.total_deposit)}
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                    {formatCurrency(ledger.total_withdrawal)}
                                </td>
                                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right' }}>
                                    {formatCurrency(ledger.next_balance)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
};

export default LedgerPanel;
